import React, { createElement, forwardRef, useRef } from "react";

import { InView } from "react-intersection-observer";

import { useHomePageId } from "@/home/useHomePage";
import useUserAttributes from "@/users/useUserAttributes";
import useTracker from "../useTracker";
import propTypes from "./withTrackEvent.constants";

const defaultProps = {
  childrenWrapperElement: "div",
  trackClick: true,
  trackHover: false,
  trackImpression: false,
  trackView: false,
};

/**
 * withTrackEvent is a higher order component (HOC) that facilitates the event
 * tracking of the new 'event-stream' kinesis streams.
 *
 * SIDE EFFECTS: This HOC wraps passed children with a div, or the HTML tag defined
 * in childrenWrapperElement prop. This might cause layout side effects for styling.
 * You might need to take into account the wrapping element.
 *
 * IMPORTANT: Components that are wrapped with this HOC will have a prop called `ref`
 * that needs to be given to a DOM element in order for the tracking to work.
 * This can be done in two ways depending on the component you're wrapping:
 *   1. Take the `props.ref` value and put it onto a DOM element in your component.
 *
 *   For example, if your wrapped component returns HTML, you can do...
 *
 *   return <div ref={props.ref}>Hello</div>
 *
 *   2. If your component doesn't return a DOM element, you'll need to make your component
 *   a .forwardRef() and pass the ref down to a component that does render a DOM element.
 *   See https://reactjs.org/docs/forwarding-refs.html for more info.
 *
 * @param  {ReactComponent} WrappedComponent
 */

export const composeEventData = (
  props,
  getEventNameForEventStream,
  homePageId,
  userAttributes,
) => {
  const userRolesString = Array.isArray(userAttributes?.user_roles)
    ? userAttributes?.user_roles.join(",")
    : null;
  const eventData = {
    event_name: getEventNameForEventStream(
      props.objectType,
      props.actionPrefix,
      props.actionName,
    ),
    dimension_properties: {
      homepage_id: homePageId,
      user_roles: userRolesString,
      ...props.streamProperties,
    },
  };

  return eventData;
};

const withTrackEvent = (WrappedComponent) => {
  const WithTrackEvent = (props) => {
    const { trackAction, getEventNameForEventStream } = useTracker();
    const homePageId = useHomePageId();
    const { userAttributes } = useUserAttributes();
    const hoverStartTime = useRef(0);

    // Handles click events
    const handleClick = (e) => {
      trackAction({
        ...composeEventData(
          props,
          getEventNameForEventStream,
          homePageId,
          userAttributes,
        ),
        event_type: "click",
      });

      if (props.onClick) {
        props.onClick(e);
      }
    };

    // Handles hover events
    const handleHoverStart = (e) => {
      hoverStartTime.current = Date.now();
      if (props.onMouseEnter) {
        props.onMouseEnter(e);
      }
    };

    const handleHoverEnd = (e) => {
      const stopTime = Date.now();
      const delta = stopTime - hoverStartTime.current;
      if (props.onMouseLeave) {
        props.onMouseLeave(e);
      }
      trackAction({
        event_type: "hover",
        hoverTime: `${delta}ms`,
        ...composeEventData(
          props,
          getEventNameForEventStream,
          homePageId,
          userAttributes,
        ),
      });
    };

    // Handles Impression events
    const handleImpression = (inView) => {
      if (!inView) return;
      const composeImpression = () => {
        // Grab href located inside each content card
        // Invoke track action
        return trackAction({
          event_type: "impression",
          ...composeEventData(
            props,
            getEventNameForEventStream,
            homePageId,
            userAttributes,
          ),
        });
      };

      composeImpression();
    };

    const trackingProps = {
      ...props,
      onClick: handleClick,
    };

    // The `data-tracker-*` attributes are useful for internal Heap
    // analytics, where the auto-capture events will also contain some of
    // surrounding HTML. While we continue using Heap auto capture, Newsela
    // Heap users can expect to use `data-tracker-*` to help define events
    // in a more stable manner. This was established in the Monolith front
    // end and for now we are keeping Lyceum at parity. Nothing aside from
    // Heap users should rely on `data-tracker-*`. If you have found
    // something else relying on this, or if you'd like to consider relying
    // on it for a new purpose, please inform the Front End Platform team.
    const heapTrackingProps = {
      "data-tracker-format": props.streamProperties?.content_type,
      "data-tracker-model": props.objectType,
    };

    if (props.trackHover) {
      trackingProps.onMouseEnter = handleHoverStart;
      trackingProps.onMouseLeave = handleHoverEnd;
    }

    if (props.trackImpression) {
      return (
        <InView
          threshold={0.75}
          as={props.childrenWrapperElement}
          onChange={handleImpression}
          {...heapTrackingProps}
        >
          <WrappedComponent {...trackingProps} ref={props.forwardedRef} />
        </InView>
      );
    }
    // If not intented to track impression, avoid wrap with InView and attach
    // a scrolling event listener, instead wrap with childrenWrapperElement
    // we need createElement because it can use any valid html tag to wrap it.
    return createElement(
      props.childrenWrapperElement || "div",
      { ...heapTrackingProps },
      <WrappedComponent {...trackingProps} ref={props.forwardedRef} />,
    );
  };

  WithTrackEvent.propTypes = propTypes;

  return forwardRef((props, ref) => (
    <WithTrackEvent {...props} forwardedRef={ref} />
  ));
};

withTrackEvent.defaultProps = defaultProps;
withTrackEvent.propTypes = propTypes;

export default withTrackEvent;
